import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authService } from '@/services/auth.service';
import { cn } from '@/utils/cn';

const schema = z.object({
  email: z.string().email('Invalid email address'),
});

type FormData = z.infer<typeof schema>;

export const ForgotPasswordPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await authService.requestPasswordReset(data.email);
      setSent(true);
    } catch {
      // Always show success to not reveal if email exists
      setSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ backgroundColor: '#FFFDF7', backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(59,130,246,0.04) 20px, rgba(59,130,246,0.04) 22px)' }}>
      <div className="absolute top-10 right-16 w-14 h-14 bg-yellow-200 border-2 border-[#1a1a2e] rounded-xl rotate-12 opacity-60" />
      <div className="absolute bottom-16 left-12 w-10 h-10 bg-blue-200 border-2 border-[#1a1a2e] rounded-full opacity-50" />
      <div className="absolute top-1/3 left-10 w-8 h-8 bg-green-200 border-2 border-[#1a1a2e] rotate-45 opacity-40" />
      <div className="w-full max-w-[420px] relative z-10">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo-icon.svg" alt="ChatCup" className="h-20 w-20" />
          <span className="text-3xl font-extrabold tracking-tight text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ChatCup</span>
        </div>

        {sent ? (
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[5px_5px_0px_#1a1a2e] p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Check your email</h2>
            <p className="text-sm text-gray-500 mb-6">
              If an account exists with that email, we've sent a password reset link.
            </p>
            <Link
              to="/login"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[5px_5px_0px_#1a1a2e] p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Forgot password?</h2>
              <p className="text-sm text-gray-500 mt-1.5">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-700">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    className={cn(
                      'w-full h-11 pl-10 pr-4 bg-white border-2 rounded-xl text-sm text-gray-900 placeholder:text-gray-400',
                      'transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      errors.email ? 'border-red-300' : 'border-[#1a1a2e]'
                    )}
                    {...register('email')}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={cn(
                  'w-full h-11 flex items-center justify-center gap-2',
                  'bg-blue-500 text-white font-bold rounded-xl border-2 border-[#1a1a2e]',
                  'shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[5px_5px_0px_#1a1a2e]',
                  'hover:translate-x-[-2px] hover:translate-y-[-2px]',
                  'transition-all duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Send reset link'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
